// Import the native module. On web, it will be resolved to BrillenSdk.web.ts
// and on native platforms to BrillenSdk.ts
import BrillenSdkModule from './src/BrillenSdkModule'
import {
  isSdkLocale,
  BrillenSdkViewLocale,
  BrillenSdkViewType,
  MeasurementResult,
  ResultEventPayload,
  SdkButtons,
} from './src/BrillenSdk.types'
import { BrillenSdkPlaceholderUi } from './src/placeholder/SdkPlaceholderUi'
import MockSdk from './src/BrillenSdkModule.mock'
import useSdkModuleStorage from './src/useSdkModuleStorage'
import Analytics from '../../src/hooks/analytics/Analytics'

export type ShowSdkViewParams = {
  type: BrillenSdkViewType
  locale: BrillenSdkViewLocale
  mock?: boolean
  translations: any
  location?: string
  pathname?: string
}

type ResultListenerSubscription = ReturnType<
  typeof BrillenSdkModule.addListener
>

let resultAnalyticsSubscription: ResultListenerSubscription | null = null

const clearAnalyticsListener = () => {
  resultAnalyticsSubscription?.remove()
  resultAnalyticsSubscription = null
}

const setupAnalyticsListener = ({
  type,
  location,
  pathname,
}: {
  type: BrillenSdkViewType
  location?: string
  pathname?: string
}) => {
  clearAnalyticsListener()

  const subscription = BrillenSdkModule.addListener('onResult', (ev) => {
    if (ev?.button === SdkButtons.save) {
      Analytics.logEvent('measure_sdk_success', {
        type: type || 'unknown',
        location: location || 'unknown',
        pathname: pathname || 'unknown',
      })
      clearAnalyticsListener()
      return
    }

    if (ev?.button === SdkButtons.close) {
      Analytics.logEvent('measure_sdk_cancel', {
        type: type || 'unknown',
        location: location || 'unknown',
        pathname: pathname || 'unknown',
      })
      clearAnalyticsListener()
    }
  })

  resultAnalyticsSubscription = subscription
}

export async function showView({
  type,
  locale,
  mock = false,
  translations,
  location,
  pathname,
}: ShowSdkViewParams) {
  const { setContext, setType } = useSdkModuleStorage.getState()
  setType(type)
  setContext({ location, pathname })

  const tlString = JSON.stringify(translations)

  // Track SDK open event
  Analytics.logEvent('measure_sdk_open', {
    type,
    location: location || 'unknown',
    pathname: pathname || 'unknown',
  })

  setupAnalyticsListener({ type, location, pathname })

  if (mock) {
    return await MockSdk.showView(type, locale, tlString)
  }
  return await BrillenSdkModule.showView(type, locale, tlString)
}

export async function prepare() {
  return await BrillenSdkModule.prepare()
}

const safeParse = (data: string) => {
  try {
    return JSON.parse(data)
  } catch (e) {
    return {}
  }
}

export function addResultListener(
  listener: (event: MeasurementResult) => void
) {
  return BrillenSdkModule.addListener('onResult', (ev) => {
    const { faceData } = ev

    const parsed = safeParse(faceData)

    listener({
      ...ev,
      faceData: parsed,
    })
  })
}

const BrillenSdkButtons = SdkButtons

export {
  ResultEventPayload,
  MeasurementResult,
  BrillenSdkPlaceholderUi,
  BrillenSdkViewLocale,
  isSdkLocale,
  BrillenSdkButtons,
}
